import { useEffect } from "react";
import { NavHeader } from "../components/NavHeader";
import { Footer } from "../components/Footer";
import { Disclaimer } from "../components/Disclaimer";

export const LegalPage = () => {
  useEffect(() => {
    document.title = "Privacy & Legal · Astrarium";
  }, []);
  return (
  <>
    <NavHeader />

    <main
      data-testid="legal-page"
      className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-12"
    >
      <p className="text-stardust/70 uppercase tracking-[0.5em] text-[10px] mb-5 text-center">The fine print</p>
      <h1 className="font-display text-4xl sm:text-5xl text-[#F5EBD8] mb-10 text-center">
        Privacy &amp; legal<span className="text-stardust">.</span>
      </h1>

      <article className="prose prose-invert max-w-none space-y-10 text-slate-300">
        <section data-testid="legal-disclaimer">
          <h2 className="font-display text-2xl text-[#F5EBD8] mb-3">For reflection, not prescription.</h2>
          <p className="leading-relaxed">
            Astrarium produces astrological readings generated with the help of artificial
            intelligence, grounded in traditional Western and Vedic astrological frameworks.
            Readings are intended for personal reflection, entertainment and curiosity.
            They are <strong>not</strong> medical, financial, legal, psychological or
            relationship advice. For decisions in those areas, please consult a qualified
            professional you trust.
          </p>
        </section>

        <section data-testid="legal-privacy">
          <h2 className="font-display text-2xl text-[#F5EBD8] mb-3">What we do with your details.</h2>
          <p className="leading-relaxed mb-3">
            To compose a reading, we use the name and birth details you enter. We do not
            require accounts, emails or passwords.
          </p>
          <ul className="space-y-2 list-none pl-0">
            <li className="flex items-start gap-2"><span className="text-stardust mt-1">✦</span><span>
              <strong>Readings you don&apos;t share</strong> live only in your browser session and
              are not stored on our servers.
            </span></li>
            <li className="flex items-start gap-2"><span className="text-stardust mt-1">✦</span><span>
              <strong>Readings you do share</strong> are stored on our server for exactly
              <strong> 7 days</strong>, after which they are automatically and permanently
              deleted. The share link is unguessable (~132 bits of entropy) but not
              password-protected, so anyone with the link can view it.
            </span></li>
            <li className="flex items-start gap-2"><span className="text-stardust mt-1">✦</span><span>
              <strong>Questions you ask the wheel</strong> are sent to our AI partner
              (Anthropic) for the sole purpose of generating an answer. They are not stored
              by Astrarium beyond your browser session unless you choose to share the reading.
            </span></li>
            <li className="flex items-start gap-2"><span className="text-stardust mt-1">✦</span><span>
              We do not sell, rent or share your data with advertisers.
            </span></li>
            <li className="flex items-start gap-2"><span className="text-stardust mt-1">✦</span><span>
              We may keep anonymous, aggregated usage metrics (page views, error counts) to
              improve the experience.
            </span></li>
          </ul>
        </section>

        <section data-testid="legal-sharing">
          <h2 className="font-display text-2xl text-[#F5EBD8] mb-3">Before you share.</h2>
          <p className="leading-relaxed">
            A shared reading includes the first name and birth details you entered. Only
            share the link with people you trust. If you&apos;d like a shared reading to be
            taken down before its 7-day expiry, please email
            {" "}<a className="text-stardust hover:text-stardust-light underline" href="mailto:hello@astrarium.app">hello@astrarium.app</a>{" "}
            with the share URL and we&apos;ll remove it promptly.
          </p>
        </section>

        <section data-testid="legal-accuracy">
          <h2 className="font-display text-2xl text-[#F5EBD8] mb-3">About accuracy.</h2>
          <p className="leading-relaxed">
            Readings are computed from your sun sign (derived from your date of birth) and
            interpreted through an AI model trained on a broad corpus of astrological,
            psychological and cultural material. If you provide a time of birth, the reading
            may gently weave in probable rising-sign influence; without it, we ground the
            reading in your sun sign, element, modality and place of birth, and never
            invent ascendant details. Astrology is a symbolic language, not a science.
          </p>
        </section>

        <section data-testid="legal-rights">
          <h2 className="font-display text-2xl text-[#F5EBD8] mb-3">Your rights.</h2>
          <p className="leading-relaxed">
            You may use Astrarium without an account and without identifying yourself.
            Because no account exists, there is little to delete on request. But if a
            shared reading is yours, write to us and we&apos;ll remove it.
          </p>
        </section>

        <section data-testid="legal-contact">
          <h2 className="font-display text-2xl text-[#F5EBD8] mb-3">Contact.</h2>
          <p className="leading-relaxed">
            For privacy questions, takedown requests or curious thoughts:
            {" "}<a className="text-stardust hover:text-stardust-light underline" href="mailto:hello@astrarium.app">hello@astrarium.app</a>.
          </p>
        </section>

        <p className="text-slate-500 text-xs italic">
          Last updated, February 2026.
        </p>
      </article>

      <Disclaimer />
    </main>

    <Footer />
  </>
  );
};
